/** Numeric wire values — mirrors EPN.Compensation.Api's own `BenefitTaxType` enum exactly
 * (Taxable=1, PreTax=2, NonTaxable=3). BenefitCode is a company-level DEFINITION owned by
 * Compensation.Api itself (a real, same-database table) — this page only lists and assigns existing
 * codes, it never creates one (see BenefitsDeductionsPage's own comment on why). */
export const benefitTaxTypeLabels: Record<number, string> = { 1: 'Taxable', 2: 'Pre-tax', 3: 'Non-taxable' };

export interface BenefitCodeResponse {
  id: string;
  name: string;
  taxType: number;
  w2Code: string | null;
  defaultEmployeeContributionPercent: number;
  defaultCompanyContributionPercent: number;
  defaultAmount: number | null;
  glAccountDefault: string | null;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  /** EPN-52: the approved tax profile code, null while the plan awaits tax review. Optional for older responses. */
  taxProfileCode?: string | null;
}

/** A per-employee, effective-dated election against a BenefitCode. Mirrors
 * EPN.Compensation.Api's BenefitElectionResponse exactly. */
export interface BenefitElectionResponse {
  id: string;
  employeeId: string;
  benefitCodeId: string;
  employeeContributionPercentOverride: number | null;
  companyContributionPercentOverride: number | null;
  amountOverride: number | null;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  concurrencyVersion?: number;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface CreateBenefitElectionInput {
  benefitCodeId: string;
  employeeContributionPercentOverride: number | null;
  companyContributionPercentOverride: number | null;
  amountOverride: number | null;
  effectiveStartDate: string;
  /** Optional: when the election stops applying (after the start date). */
  effectiveEndDate?: string | null;
  /** True when this replaces the employee's current election of the plan; the server refuses a second open one otherwise (409 DUPLICATE_ACTIVE_ASSIGNMENT). */
  replaceCurrent?: boolean;
}

/** A per-employee, effective-dated election against a company-level DeductionCode — DeductionCode
 * itself is owned by EPN.PayrollConfig.Api (see the pay-config feature's PayConfigGateway) and is
 * only read here, never created. Mirrors EPN.Compensation.Api's DeductionElectionResponse exactly. */
export interface DeductionElectionResponse {
  id: string;
  employeeId: string;
  deductionCodeId: string;
  amountOverride: number | null;
  companyContributionOverride: number | null;
  /** Optional admin label for this employee's assignment (display only). */
  title: string | null;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  /** The employee's own annual limit for this deduction. Recorded and shown only: payroll does not enforce limits yet. */
  employeeAnnualLimit?: number | null;
  concurrencyVersion?: number;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface CreateDeductionElectionInput {
  deductionCodeId: string;
  amountOverride: number | null;
  companyContributionOverride: number | null;
  title?: string | null;
  effectiveStartDate: string;
  /** Optional: when the assignment stops applying (after the start date). */
  effectiveEndDate?: string | null;
  /** Optional annual limit for this employee (not more than the deduction's own annual limit when it has one). */
  employeeAnnualLimit?: number | null;
  /** True when this replaces the employee's current assignment of the deduction; the server refuses a second open one otherwise (409 DUPLICATE_ACTIVE_ASSIGNMENT). */
  replaceCurrent?: boolean;
}

/** Numeric wire values — mirrors EPN.Compensation.Api's `CompensationPayType` enum exactly. 1 to 5 are the original values (Commission, Tips and
 * Executive bonus stay valid in data and keep rendering, but are not offered for a new assignment); 6 to 9 are the pay bases added for healthcare
 * (docs/healthcare-discipline-compensation-contract.md, section 4). The mapping to the string pay basis lives in healthcare-rates/wireMapping.ts. */
export const compensationPayTypeLabels: Record<number, string> = {
  1: 'Hourly', 2: 'Salary', 3: 'Commission', 4: 'Tips', 5: 'Executive bonus', 6: 'Per Visit', 7: 'Per Shift', 8: 'Daily', 9: 'Per Unit/Case',
};

/** A per-employee, effective-dated pay rate. Mirrors EPN.Compensation.Api's WorkRateAssignmentResponse. */
export interface WorkRateAssignmentResponse {
  id: string;
  employeeId: string;
  workRateId: string | null;
  label: string;
  payType: number;
  rate: number;
  isPrimary: boolean;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface CreateWorkRateAssignmentInput {
  workRateId: string | null;
  label: string;
  payType: number;
  rate: number;
  isPrimary: boolean;
  effectiveStartDate: string;
}
